// Epic 7 re-skin (ADO-903) — CollapsibleFilterSidebar.
//
// Ported from the designer prototype's `collapsible-filter-sidebar` (Next/Tailwind
// → our React + CSS Modules idiom). The collapsing filter panel ONLY — the
// "Filters" label + toggle live in the parent toolbar row (next to the
// breadcrumb), exactly as in the prototype. When `open` the panel is a fixed
// 264px-wide column with its own vertical scroll; when closed it animates to zero
// width. The parent positions it (e.g. sticky, full viewport height) so the panel
// stays put while the product grid scrolls.
//
// a11y: an `<aside aria-label="Product filters">` landmark; when closed it is
// `aria-hidden` AND `visibility: hidden` (see the module CSS), so its controls
// leave the keyboard tab order rather than being merely visually collapsed — a
// focusable control inside an aria-hidden container is a WCAG 4.1.2 violation
// (PR #139 review).

import type { ReactNode } from 'react';
import styles from './CollapsibleFilterSidebar.module.css';

export interface CollapsibleFilterSidebarProps {
  /** Whether the panel is open (controlled by the parent's Filters toggle). */
  readonly open: boolean;
  /** Panel content (e.g. the FilterPanel). */
  readonly children: ReactNode;
  /** Optional class on the wrapper — the parent uses it to position/size the rail. */
  readonly className?: string | undefined;
  /**
   * Epic 7 re-skin (ADO-903) — render the prototype's inert "Advertisement
   * Space" placeholder box below the panel content. Chrome only (customer demo);
   * never a live ad surface.
   */
  readonly showAdPlaceholder?: boolean;
}

export function CollapsibleFilterSidebar({
  open,
  children,
  className,
  showAdPlaceholder = false,
}: CollapsibleFilterSidebarProps) {
  return (
    <div className={[styles.root, className].filter(Boolean).join(' ')}>
      <aside
        aria-label="Product filters"
        className={`${styles.aside} ${open ? styles.open : styles.closed}`}
        aria-hidden={!open}
      >
        <div className={styles.scroll}>
          {children}
          {showAdPlaceholder && (
            <div className={styles.adPlaceholder}>
              <p className={styles.adPlaceholderText}>Advertisement Space</p>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
